import {
  Button,
  Card,
  CardContent,
  CardDescription,
  CardFooter,
  CardHeader,
  CardTitle,
  Form,
} from "@sfc/ui";
import { FormInput } from "@client/components/forms/form-input";
import { trpcVanilla } from "@client/trpc";
import { zodResolver } from "@hookform/resolvers/zod";
import { ArrowRightIcon } from "lucide-react";
import { useState } from "react";
import { useForm } from "react-hook-form";
import { twMerge } from "tailwind-merge";
import { RouteScroller } from "@client/components/utils/RouteScroller";
import * as z from "zod";

const schema = z.object({
  name: z.string().min(1, "Name is required"),
});

type FormValues = z.infer<typeof schema>;

export type CompleteProfileProps = React.ComponentProps<"div">;

export function CompleteProfile({ className, ...rest }: CompleteProfileProps) {
  const [loading, setLoading] = useState(false);
  const form = useForm<FormValues>({
    resolver: zodResolver(schema),
    defaultValues: {
      name: "",
    },
  });

  async function onSubmit(values: FormValues) {
    setLoading(true);
    try {
      await trpcVanilla.updateProfile.mutate({
        name: values.name,
      });
      // Reload to pick up the new session cookie with the user's name.
      // The Cookie Store API doesn't fire change events for httpOnly cookies,
      // so we need to reload to get AuthWrapper to re-read the session.
      window.location.reload();
    } catch (error) {
      console.error(error);
      setLoading(false);
    }
  }

  return (
    // The shell is viewport-locked, so this screen owns its scroller like any
    // route: centered when it fits, scrollable (not clipped) when it doesn't.
    <RouteScroller>
      <div
        className={twMerge(
          "min-h-full flex items-center justify-center",
          className,
        )}
        {...rest}
      >
        <Form {...form}>
          <Card className="w-full max-w-[calc(100%-2rem)] sm:max-w-md shadow-xl text-center">
            <form onSubmit={form.handleSubmit(onSubmit)}>
              <CardHeader>
                <CardTitle>Welcome to S-Process</CardTitle>
                <CardDescription>
                  Let's get you set up with a quick introduction. What should we
                  call you?
                </CardDescription>
              </CardHeader>
              <CardContent className="flex flex-col gap-6">
                <FormInput
                  control={form.control}
                  name="name"
                  label="Name"
                  placeholder="e.g., John Doe"
                />
                <p className="text-xs text-muted-foreground">
                  Your name will be used to identify you in rounds and when
                  collaborating with team members.
                </p>
              </CardContent>
              <CardFooter>
                <Button
                  className="flex-1"
                  size="lg"
                  type="submit"
                  isLoading={loading}
                >
                  Continue to S-Process <ArrowRightIcon />
                </Button>
              </CardFooter>
            </form>
          </Card>
        </Form>
      </div>
    </RouteScroller>
  );
}
